import cx from 'classnames'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useClickOutside } from '../../../hooks'
import * as passiveEvents from '../../../lib/passive-events'
import { SearchInput } from '../components/search-input'

import styles from './filters-dropdown.module.css'

interface Option {
  value: string | number | boolean
  label?: React.ReactNode
  searchBy?: string
  helperText?: string
}

interface Props {
  value: Array<string | number | boolean>
  options: Option[]
  onChange: (value: Array<string | number | boolean>) => void
  onClose: () => void
  radio?: boolean
  hideSearch?: boolean
}

export function FiltersDropdown ({ value, options, onChange, onClose, radio = false, hideSearch = false }: Props): JSX.Element {
  let [ searchQuery, setSearchQuery ] = useState('')
  let [ horizontalShift, setHorizontalShift ] = useState(0)

  let normalizedSearchQuery = searchQuery
    .toLowerCase()
    .trim()

  let filteredOptions = options.filter(option => {
    if (!normalizedSearchQuery.length) {
      return true
    }

    let searchBy =
      option.searchBy ??
      (typeof option.label === 'string' ? option.label : undefined) ??
      option.value.toString(10)

    let normalized = searchBy.toLocaleLowerCase()
    return normalized.includes(normalizedSearchQuery)
  })

  let wrapperRef = useRef<HTMLDivElement | null>(null)

  let adjustHorizontalShift = useCallback((): void => {
    if (!wrapperRef.current) {
      setHorizontalShift(0)
      return
    }

    let rect = wrapperRef.current.getBoundingClientRect()
    setHorizontalShift(Math.min(0, window.innerWidth - (rect.x + rect.width + 64)))
  }, [])

  useLayoutEffect(() => {
    if (!wrapperRef.current) {
      return
    }

    adjustHorizontalShift()
  }, [ adjustHorizontalShift ])

  useEffect(() => {
    window.addEventListener('resize', adjustHorizontalShift, passiveEvents.options)

    return () => {
      window.removeEventListener('resize', adjustHorizontalShift, passiveEvents.options)
    }
  }, [ adjustHorizontalShift ])

  useClickOutside(wrapperRef, () => {
    onClose()
  })

  return (
    <div
      className={styles.wrapper}
      ref={wrapperRef}
      style={{
        transform: `translate(${horizontalShift}px, calc(100% - 0.5rem))`
      }}
    >
      <div className={cx(styles.content, value.length > 0 && styles.withToolbar)}>
        {!hideSearch && (
          <div className={styles.searchInputContainer}>
            <SearchInput
              placeholder='Search'
              onChange={setSearchQuery}
              value={searchQuery}
            />
          </div>
        )}

        <div>
          <div className={styles.scrollableArea}>
            {filteredOptions.map((option, i) => (
              <div
                key={option.value.toString(10)}
                className={styles.section}
              >
                <label>
                  <input
                    type={radio ? 'radio' : 'checkbox'}
                    name={radio ? 'filter-radio-group' : undefined}
                    checked={radio ? value[0] === option.value : value.includes(option.value)}
                    onChange={event => {
                      if (radio) {
                        onChange([option.value])
                      } else {
                        onChange(
                          event.target.checked
                            ? [ ...value, option.value ]
                            : value.filter(value => value !== option.value)
                        )
                      }
                    }}
                  />
                  <span className={styles.label}>
                    {option.label ?? option.value}
                    {typeof option.helperText === 'string' && (
                      <span className={styles.helperText}>
                        {option.helperText}
                      </span>
                    )}
                  </span>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      {value.length > 0 && (
        <div>
          <button
            type='button'
            className={styles.clearFiltersButton}
            onClick={() => {
              onChange([])
              setSearchQuery('')
            }}
          >
            Clear filter
          </button>
        </div>
      )}
    </div>
  )
}

interface FiltersDropdownWrapperProps {
  children: React.ReactNode
  onClose: () => void
  clearButton?: React.ReactNode
}

export function FiltersDropdownWrapper ({ children, onClose, clearButton }: FiltersDropdownWrapperProps): JSX.Element {
  let wrapperRef = useRef<HTMLDivElement | null>(null)
  let [ horizontalShift, setHorizontalShift ] = useState(0)

  let adjustHorizontalShift = useCallback((): void => {
    if (!wrapperRef.current) {
      setHorizontalShift(0)
      return
    }

    let rect = wrapperRef.current.getBoundingClientRect()
    setHorizontalShift(Math.min(0, window.innerWidth - (rect.x + rect.width + 64)))
  }, [])

  useLayoutEffect(() => {
    if (!wrapperRef.current) {
      return
    }

    adjustHorizontalShift()
  }, [ adjustHorizontalShift ])

  useEffect(() => {
    window.addEventListener('resize', adjustHorizontalShift, passiveEvents.options)

    return () => {
      window.removeEventListener('resize', adjustHorizontalShift, passiveEvents.options)
    }
  }, [ adjustHorizontalShift ])

  useClickOutside(wrapperRef, () => {
    onClose()
  })

  return (
    <div
      className={styles.wrapper}
      ref={wrapperRef}
      style={{
        transform: `translate(${horizontalShift}px, calc(100% - 0.5rem))`
      }}
    >
      <div className={cx(styles.content, clearButton && styles.withToolbar)}>
        <div>
          <div className={styles.scrollableArea}>
            {children}
          </div>
        </div>
      </div>
      {clearButton && (
        <div>
          {clearButton}
        </div>
      )}
    </div>
  )
}
